import SidebarAddPageButton from "pages/notebooks/pages/sidebar-add-page-button";
import CollapsePanel from "molecules/collapse-panel/collapse-panel";
import React from "react";
import { IconSizes } from "atoms/constants/icon-sizes";
import { t } from "utilities/localization-utils";
import NotebookPanel from "./notebook-panel";
import PagePanel from "./page-panel";
import SharedPagePanel from "./shared-page-panel";
import NotebookRecord from "models/view-models/notebook-record";

const BASE_CLASS_NAME = "c-notebook-sidebar";

interface NotebookListSectionProps {
    notebooks: NotebookRecord[];
    buttonAriaText: string;
    panelTop: React.ReactNode;
    collapse: boolean;
    openNotebookIds: number[];
    getNotebookClassModifier: (notebookId: number) => string;
    onSectionOpen: () => void;
    onSectionClose: () => void;
    onNotebookTitleClick: (
        id: number,
        isOpen: boolean,
        shouldExpand: boolean
    ) => void;
    onNotebookExpand: (id: number, isOpen: boolean) => void;
    onNotebookClose: (id: number) => void;
    isSharedSection?: boolean;
    isSharedPagesNotebook: (notebook: NotebookRecord) => boolean;
}

const NotebookListSection = ({
    notebooks,
    buttonAriaText,
    panelTop,
    collapse,
    openNotebookIds,
    getNotebookClassModifier,
    onSectionOpen,
    onSectionClose,
    onNotebookTitleClick,
    onNotebookExpand,
    onNotebookClose,
    isSharedSection = false,
    isSharedPagesNotebook,
}: NotebookListSectionProps) => {
    return (
        <div className={`${BASE_CLASS_NAME}__section`}>
            <CollapsePanel
                buttonAriaText={buttonAriaText}
                collapse={collapse}
                iconSize={IconSizes.Base}
                panelTop={panelTop}
                onOpen={onSectionOpen}
                onClose={onSectionClose}>
                {notebooks.map((notebook) => {
                    const notebookId = Number(notebook.id);
                    const isOpen = openNotebookIds.includes(notebookId);
                    const hasNoPages = notebook?.pages?.length === 0;
                    const shouldExpand = !(isSharedSection && hasNoPages);

                    return (
                        <div
                            className={`${BASE_CLASS_NAME}__item ${getNotebookClassModifier(notebookId)} ${isOpen ? "-open" : ""}`}
                            key={notebook.id}>
                            <CollapsePanel
                                buttonAriaText={`toggle ${notebook.name}`}
                                collapse={!isOpen}
                                iconSize={IconSizes.Base}
                                panelTop={<NotebookPanel notebook={notebook} />}
                                onTitleClick={() =>
                                    onNotebookTitleClick(
                                        notebookId,
                                        isOpen,
                                        shouldExpand
                                    )
                                }
                                onOpen={() =>
                                    onNotebookExpand(notebookId, isOpen)
                                }
                                onClose={() => onNotebookClose(notebookId)}>
                                {notebook.pages?.map((page) =>
                                    isSharedSection ? (
                                        isSharedPagesNotebook(notebook) ? (
                                            <SharedPagePanel
                                                key={`shared-${page.id}-${page.name}`}
                                                page={page}
                                                notebook={notebook}
                                            />
                                        ) : (
                                            <PagePanel
                                                key={`${page.id}-${page.name}`}
                                                page={page}
                                                notebookId={notebook.id!}
                                            />
                                        )
                                    ) : (
                                        <PagePanel
                                            key={`${page.id}-${page.name}`}
                                            page={page}
                                            notebookId={notebookId}
                                        />
                                    )
                                )}
                                {isSharedSection &&
                                    isSharedPagesNotebook(notebook) &&
                                    hasNoPages && (
                                        <div
                                            className={`${BASE_CLASS_NAME}__sharedPage`}>
                                            <span>
                                                {t(
                                                    "notebooks-shared-pages-placeholder"
                                                )}
                                            </span>
                                        </div>
                                    )}
                                {!isSharedSection && (
                                    <div
                                        className={`${BASE_CLASS_NAME}__addPageButton`}>
                                        <SidebarAddPageButton
                                            notebookId={notebookId}
                                        />
                                    </div>
                                )}
                            </CollapsePanel>
                        </div>
                    );
                })}
            </CollapsePanel>
        </div>
    );
};

export default NotebookListSection;
